/**
 * CatalogViewer — public share page aligned with Figma (logos, banner, product grid).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, IconButton, Dialog } from '@mui/material';
import { PlayCircleOutline, Close } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { publicCatalogAPI, publicCatalogStreamUrl } from '../../../services/merchantAPI';
import { catalogColors, publicViewerTheme } from './catalogTheme';
import { API_URLS } from '../../../config/apiConfig';

const GLAMMIFY_LOGO = publicViewerTheme.glammifyLogo;

/** Left header: merchant name two-line gold lockup (RAMETI / JEWELS). */
function MerchantBrandHeader({ brandLogoUrl }) {
  // Display brand logo if available, otherwise render empty
  if (!brandLogoUrl) {
    return <Box sx={{ height: { xs: 24, sm: 40, md: 45 }, width: 'auto' }} />;
  }

  return (
    <Box
      component="img"
      src={brandLogoUrl}
      alt="Brand Logo"
      sx={{
        height: { xs: 50, sm: 70, md: 70 },
        width: 'auto',
        objectFit: 'contain',
      }}
    />
  );
}

/** Right header: Glammify logo (homepage style). */
function GlammifyBrandHeader() {
  return (
    <Box
      component="img"
      src="/assets/img/glammify-logo.png"
      alt="Glammify — Where Glamour Meets AI"
      sx={{
        height: { xs: 50, sm: 70, md: 70 },
        width: 'auto',
        objectFit: 'contain',
        objectPosition: 'right center',
      }}
    />
  );
}

/** Grid thumb: try kind=thumb, on 404 fall back to kind=image (original). */
function PublicCatalogThumb({ thumbnailUrl, alt }) {
  const [src, setSrc] = useState(thumbnailUrl);
  const usedFallback = useRef(false);

  useEffect(() => {
    setSrc(thumbnailUrl);
    usedFallback.current = false;
  }, [thumbnailUrl]);

  const onError = () => {
    if (usedFallback.current || !thumbnailUrl) return;
    try {
      const u = new URL(thumbnailUrl);
      if (u.searchParams.get('kind') !== 'thumb') return;
      u.searchParams.set('kind', 'image');
      usedFallback.current = true;
      setSrc(u.toString());
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      onError={onError}
      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  );
}

function PublicProductCard({ item, onZoom }) {
  const cap1 = item.caption_1 !== 'NA' ? item.caption_1 : null;
  const cap2 = item.caption_2 !== 'NA' ? item.caption_2 : null;
  const altLabel = cap1 || cap2 || 'Product';

  return (
    <Box sx={{
      width: '100%',
      borderRadius: '8px',
      overflow: 'hidden',
      bgcolor: '#fff',
      pt: '20px',
      px: '20px',
      pb: '20px',
      boxSizing: 'border-box',
    }}>
      <Box
        onClick={() => onZoom(item)}
        sx={{
          cursor: item.media_type === 'video' ? 'default' : 'zoom-in',
          height: 385,
          width: '100%',
          mb: 2,
          position: 'relative',
        }}
      >
        {item.thumbnail_url ? (
          <PublicCatalogThumb thumbnailUrl={item.thumbnail_url} alt={altLabel} />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlayCircleOutline sx={{ fontSize: 40, color: 'rgba(255,255,255,0.5)' }} />
          </Box>
        )}
        {item.media_type === 'video' && (
          <PlayCircleOutline
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#fff',
              fontSize: 48,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      {cap1 && (
        <Typography
          sx={{
            fontFamily: 'var(--font-freeserif)',
            fontWeight: 400,
            fontSize: { xs: 16, sm: 20, md: 20 },
            color: '#B87F0A',
            mb: 0,
            lineHeight: 1.35,
            textAlign: 'left',
          }}
        >
          {cap1}
        </Typography>
      )}

      {cap2 && (
        <Typography
          sx={{
            fontFamily: 'var(--font-freesans)',
            fontWeight: 400,
            fontSize: { xs: 16, sm: 20, md: 20 },
            color: '#000000',
            lineHeight: 1.35,
            mb: 0,
            textAlign: 'left',
          }}
        >
          {cap2}
        </Typography>
      )}
      {!cap1 && !cap2 && (
        <Typography sx={{ fontSize: 11, color: catalogColors.textMuted, mt: 1, fontStyle: 'italic', textAlign: 'left' }}>
          NA
        </Typography>
      )}
    </Box>
  );
}

// Full-screen zoom (was ZoomViewer.js)
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.25;

/** Same host as grid thumbnails (API host_url), only changes stream kind. */
function catalogStreamKindUrl(thumbnailUrl, itemId, token, kind) {
  if (thumbnailUrl) {
    try {
      const u = new URL(thumbnailUrl);
      u.searchParams.set('kind', kind);
      return u.toString();
    } catch {
      /* fall through */
    }
  }
  return publicCatalogStreamUrl(token, itemId, kind);
}

function CatalogZoomOverlay({ open, item, token, onClose }) {
  const [loadedItemId, setLoadedItemId] = useState(null);
  const [error, setError] = useState('');
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const initialDist = useRef(null);
  const baseScale = useRef(1);
  const baseTranslate = useRef({ x: 0, y: 0 });
  const activeItemIdRef = useRef(null);

  const itemId = item?.id ?? null;
  const mediaType = item?.media_type || 'image';
  // Derive URLs from current item on every render — avoids flashing the previous image.
  const fullUrl = open && item && token
    ? catalogStreamKindUrl(item.thumbnail_url, item.id, token, 'image')
    : null;
  const videoUrl = open && item && mediaType === 'video'
    ? catalogStreamKindUrl(item.thumbnail_url, item.id, token, 'video')
    : null;
  const loading = Boolean(open && itemId && loadedItemId !== itemId);

  useEffect(() => {
    activeItemIdRef.current = itemId;
  }, [itemId]);

  useEffect(() => {
    if (!open || !item) {
      setLoadedItemId(null);
      setError('');
      return;
    }
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setError('');
    setLoadedItemId(null);
  }, [open, itemId, token]);

  const markItemLoaded = useCallback(() => {
    if (activeItemIdRef.current === itemId) {
      setLoadedItemId(itemId);
    }
  }, [itemId]);

  const markItemFailed = useCallback((message) => {
    if (activeItemIdRef.current === itemId) {
      setLoadedItemId(itemId);
      setError(message);
    }
  }, [itemId]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const resetTransform = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setScale(s => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta)));
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      isDragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      baseTranslate.current = translate;
    } else if (pointers.current.size === 2) {
      isDragging.current = false;
      const [p1, p2] = [...pointers.current.values()];
      initialDist.current = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      baseScale.current = scale;
    }
  }, [scale, translate]);

  const handlePointerMove = useCallback((e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && initialDist.current) {
      const [p1, p2] = [...pointers.current.values()];
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, baseScale.current * (dist / initialDist.current))));
    } else if (pointers.current.size === 1 && isDragging.current && scale > 1) {
      setTranslate({
        x: baseTranslate.current.x + (e.clientX - dragStart.current.x),
        y: baseTranslate.current.y + (e.clientY - dragStart.current.y),
      });
    }
  }, [scale]);

  const handlePointerUp = useCallback((e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) initialDist.current = null;
    if (pointers.current.size === 0) isDragging.current = false;
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2, md: 0 },
          padding: { xs: 0.5, sm: 1, md: 20 },
          backgroundColor: 'transparent',
          boxShadow: 'none',
          maxWidth: 'none',
          maxHeight: 'none',
          width: 'auto',
          height: 'auto',
          overflow: 'visible'
        }
      }}
    >
      <Box sx={{
        position: 'relative', display: 'inline-block',
        // Reserve space so the spinner is centred before the media has sized.
        minWidth: loading ? 280 : 'auto',
        minHeight: loading ? 280 : 'auto',
      }}>
        {loading && !error && (
          <CircularProgress sx={{
            color: '#A864ED', position: 'absolute',
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 6,
          }} />
        )}
        {error && (
          <Typography sx={{ color: '#FFB3B3', p: 4, textAlign: 'center' }}>{error}</Typography>
        )}
        {/* The media is always mounted (when a URL exists) so its load/error events
            actually fire; it just stays hidden behind the spinner until ready. */}
        {!error && (fullUrl || videoUrl) && (
          <Box
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onDoubleClick={resetTransform}
            sx={{
              transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
              transformOrigin: 'center center',
              cursor: scale > 1 ? 'grab' : 'zoom-in',
              maxWidth: '85vw',
              maxHeight: '85vh',
              display: 'block',
              opacity: loading ? 0 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {mediaType === 'video' && videoUrl ? (
              <Box
                component="video"
                src={videoUrl}
                controls
                playsInline
                autoPlay
                muted
                onLoadedData={markItemLoaded}
                onError={() => markItemFailed('Failed to load video')}
                sx={{
                  maxWidth: '85vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  borderRadius: '10px',
                  backgroundColor: '#000',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                component="img"
                src={fullUrl}
                alt=""
                draggable={false}
                onLoad={markItemLoaded}
                onError={() => markItemFailed('Failed to load image')}
                sx={{
                  maxWidth: '85vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  borderRadius: '10px',
                  backgroundColor: '#000',
                  display: 'block',
                }}
              />
            )}
          </Box>
        )}

        {/* Close Button - Positioned outside the image/video */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: -25,
            right: -25,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.8)',
            },
            width: 40,
            height: 40,
            borderRadius: '50%',
            zIndex: 10,
          }}
        >
          <Close sx={{ fontSize: 25 }} />
        </IconButton>
      </Box>
    </Dialog>
  );
}

export default function CatalogViewer() {
  const { token } = useParams();
  const [catalog, setCatalog] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoomItem, setZoomItem] = useState(null);

  useEffect(() => {
    if (!token) return;

    // Direct PDF generator endpoint check
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('pdf') === 'true') {
      window.location.href = `${API_URLS.BACKEND_BASE}/api/public/catalog/${token}/pdf`;
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    publicCatalogAPI.getCatalog(token)
      .then(res => {
        if (cancelled) return;
        setCatalog(res.data.catalog);
        setItems(res.data.items || []);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.response?.data?.error || 'This catalog link is invalid or has expired.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: catalogColors.pageOuter }}>
        <CircularProgress sx={{ color: catalogColors.purple }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: catalogColors.pageOuter }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400, bgcolor: '#fff', p: 4, borderRadius: 2 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>Link Not Available</Typography>
          <Typography sx={{ fontSize: 14, color: catalogColors.textSecondary }}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (!catalog || items.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: catalogColors.pageOuter }}>
        <Typography sx={{ fontSize: 16, color: catalogColors.textSecondary }}>
          {catalog?.name || 'Catalog'} has no items yet.
        </Typography>
      </Box>
    );
  }

  const brandLine1 = catalog?.brand_line1 || catalog?.name?.split(' ')[0]?.toUpperCase() || 'CATALOG';
  const brandLine2 = catalog?.brand_line2 || (catalog?.name?.includes(' ') ? catalog?.name.split(' ').slice(1).join(' ').toUpperCase() : '');

  return (
    <Box
      className='catalog-viewer'
      sx={{
        minHeight: '100vh',
        bgcolor: '#fff',
        py: { xs: 2, md: 4 },
        px: { xs: 1.5, md: 3 },
        '@media print': { bgcolor: '#fff', py: 0, px: 0 },
      }}
    >
      {/* Main container - no border radius and no shadow */}
      <Box
        sx={{
          maxWidth: 1800,
          mx: 'auto',
          bgcolor: 'transparent',
          borderRadius: 0,
          boxShadow: 'none',
          overflow: 'hidden',
          '@media print': { boxShadow: 'none', borderRadius: 0, maxWidth: '100%' },
        }}
      >
        {/* Header: merchant left, Glammify right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            gap: 2,
            px: { xs: 0.5, md: 0.5 },
            py: { xs: 0, md: 0 },
            bgcolor: '#fff',
          }}
        >
          <MerchantBrandHeader brandLogoUrl={catalog?.brand_logo_url} />
          <GlammifyBrandHeader />
        </Box>

        {/* Catalog details: name & description */}
        <Box
          sx={{
            px: { xs: 0, md: 0 },
            pt: { xs: 3, md: 5 },
            pb: { xs: 2, md: 4 },
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
              mb: 0,
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: "var(--font-montserrat)",
                fontWeight: 500,
                fontSize: { xs: 18, sm: 30, md: 37 },
                color: '#C5A059',
                mb: 1,
                lineHeight: 1.2,
                flex: 1,
                textAlign: 'left',
              }}
            >
              {catalog?.name || 'Catalog'}
            </Typography>
          </Box>
          {catalog?.description && (
            <Typography
              sx={{
                fontFamily: 'var( --font-montserrat)',
                fontWeight: 400,
                fontSize: { xs: 12, sm: 14, md: 17 },
                color: '#4A4455',
                lineHeight: 1.6,
                textAlign: 'left',
              }}
            >
              {catalog.description}
            </Typography>
          )}
        </Box>

        {/* Product grid: one by one on mobile, 4 columns on desktop with #F1F1F1 background */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pb: { xs: 4, md: 3 },
            pt: { xs: 3, md: 3 },
            bgcolor: '#F1F1F1',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(5, 1fr)',
            },
            gap: { xs: 3, md: 4.2 },
            columnGap: { xs: 2, md: 3.5 },
          }}
        >
          {items.map(item => (
            <PublicProductCard key={item.id} item={item} onZoom={setZoomItem} />
          ))}
        </Box>
      </Box>

      <CatalogZoomOverlay
        key={zoomItem?.id ?? 'closed'}
        open={Boolean(zoomItem)}
        item={zoomItem}
        token={token}
        onClose={() => setZoomItem(null)}
      />
    </Box>
  );
}
